import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import { Save, Building, Phone, Mail, MapPin, Globe, Image as ImageIcon, Upload, X, FileText, ExternalLink, Clock, Scale, Tag, ArrowRight, Server } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Link } from "react-router-dom";

const DEFAULT_HERO_BG = "/hero-loroux.jpg";

export const AdminSettings = () => {
  const { token } = useAuth();
  const { refreshSettings } = useSettings();
  const heroInputRef = useRef(null);
  const feesInputRef = useRef(null);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingFees, setUploadingFees] = useState(false);
  const [settings, setSettings] = useState({
    agency_name: "",
    address: "",
    phone: "",
    email: "",
    description: "",
    facebook: "",
    instagram: "",
    linkedin: "",
    hero_image: "",
    fees_pdf: "",
    fees_pdf_name: "",
    hours: "",
    legal_notice: "",
    privacy_policy: "",
    email2: "",
    hosting_eu: false,
    hosting_provider: "",
    hosting_location: "",
    hosting_database: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  useEffect(() => {
    fetchSettings();
  }, [token]);
  
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await axios.put(`${API}/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (refreshSettings) await refreshSettings();
      toast.success("Paramètres enregistrés avec succès !");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };
  
  const handleFeesFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingFees(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/upload/pdf`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, fees_pdf: res.data.url, fees_pdf_name: res.data.filename }));
      toast.success("PDF des honoraires chargé. N'oubliez pas d'enregistrer.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi du PDF");
    } finally {
      setUploadingFees(false);
    }
  };

  const handleHeroFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingHero(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await axios.post(`${API}/upload/image`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({ ...prev, hero_image: res.data.url }));
      toast.success("Image d'accueil chargée. N'oubliez pas d'enregistrer.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi de l'image");
    } finally {
      setUploadingHero(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="space-y-6 max-w-3xl" data-testid="admin-settings-page">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl text-[#0A0A0A]">Paramètres du site</h1>
          <p className="text-gray-500 mt-1">Modifiez les informations de votre agence</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Agency Info */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building size={20} className="text-[#D4AF37]" />
                Informations de l'agence
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="agency_name">Nom de l'agence</Label>
                <Input
                  id="agency_name"
                  value={settings.agency_name}
                  onChange={(e) => setSettings(prev => ({ ...prev, agency_name: e.target.value }))}
                  placeholder="MAINGUY IMMOBILIER"
                  data-testid="settings-agency-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  rows={3}
                  value={settings.description}
                  onChange={(e) => setSettings(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Votre partenaire de confiance pour tous vos projets immobiliers"
                  data-testid="settings-description"
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Contact Info */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Phone size={20} className="text-[#D4AF37]" />
                Coordonnées
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="phone"
                      value={settings.phone}
                      onChange={(e) => setSettings(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="05 56 00 00 00"
                      className="pl-10"
                      data-testid="settings-phone"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={settings.email}
                      onChange={(e) => setSettings(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="contact@mainguy-immobilier.fr"
                      className="pl-10"
                      data-testid="settings-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email2">Seconde adresse e-mail (interne, réception des dossiers déposés)</Label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      id="email2"
                      type="email"
                      value={settings.email2 || ""}
                      onChange={(e) => setSettings(prev => ({ ...prev, email2: e.target.value }))}
                      placeholder="adresse interne (non affichée sur le site)"
                      className="pl-10"
                      data-testid="settings-email2"
                    />
                  </div>
                  <p className="text-xs text-gray-500">Non affichée sur le site. Les dossiers déposés via « Déposer un dossier » sont notifiés à cette adresse (sinon à l'adresse principale).</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">Adresse</Label>
                <div className="relative">
                  <MapPin size={16} className="absolute left-3 top-3 text-gray-400" />
                  <Textarea
                    id="address"
                    rows={2}
                    value={settings.address}
                    onChange={(e) => setSettings(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="123 Rue de la Vigne, 33000 Bordeaux"
                    className="pl-10"
                    data-testid="settings-address"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="hours">Horaires d'ouverture</Label>
                <div className="relative">
                  <Clock size={16} className="absolute left-3 top-3 text-gray-400" />
                  <Textarea
                    id="hours"
                    rows={3}
                    value={settings.hours || ""}
                    onChange={(e) => setSettings(prev => ({ ...prev, hours: e.target.value }))}
                    placeholder={"Lundi - Vendredi : 9h - 19h\nSamedi : 10h - 18h\nDimanche : Sur rendez-vous"}
                    className="pl-10"
                    data-testid="settings-hours"
                  />
                </div>
                <p className="text-xs text-gray-500">Une ligne par plage horaire. Affiché dans le pied de page et sur la page Contact.</p>
              </div>
            </CardContent>
          </Card>
          
          {/* Social Networks */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Globe size={20} className="text-[#D4AF37]" />
                Réseaux sociaux
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="facebook">Facebook</Label>
                <Input
                  id="facebook"
                  value={settings.facebook || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, facebook: e.target.value }))}
                  placeholder="https://facebook.com/mainguy-immobilier"
                  data-testid="settings-facebook"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="instagram">Instagram</Label>
                <Input
                  id="instagram"
                  value={settings.instagram || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, instagram: e.target.value }))}
                  placeholder="https://instagram.com/mainguy_immobilier"
                  data-testid="settings-instagram"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="linkedin">LinkedIn</Label>
                <Input
                  id="linkedin"
                  value={settings.linkedin || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, linkedin: e.target.value }))}
                  placeholder="https://linkedin.com/company/mainguy-immobilier"
                  data-testid="settings-linkedin"
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Hero image */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <ImageIcon size={20} className="text-[#D4AF37]" />
                Image d'accueil
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-gray-100 aspect-[21/9]">
                <img
                  src={settings.hero_image || DEFAULT_HERO_BG}
                  alt="Aperçu de l'image d'accueil"
                  className="w-full h-full object-cover"
                  data-testid="settings-hero-preview"
                />
                {settings.hero_image && (
                  <button
                    type="button"
                    onClick={() => setSettings(prev => ({ ...prev, hero_image: "" }))}
                    className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors"
                    title="Revenir à l'image par défaut"
                    data-testid="settings-hero-reset"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <input
                ref={heroInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={handleHeroFile}
                data-testid="settings-hero-file-input"
              />
              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed border-2 h-14 hover:border-[#D4AF37] hover:text-[#D4AF37]"
                onClick={() => heroInputRef.current?.click()}
                disabled={uploadingHero}
                data-testid="settings-hero-upload-button"
              >
                <Upload size={18} className="mr-2" />
                {uploadingHero ? "Envoi en cours..." : "Télécharger l'image d'accueil (JPG, PNG, WEBP – 10 Mo max)"}
              </Button>
              <p className="text-xs text-gray-500">Format paysage recommandé (au moins 1920 × 1080 px).</p>
            </CardContent>
          </Card>

          {/* Fees PDF */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileText size={20} className="text-[#D4AF37]" />
                Honoraires (PDF)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-500">Ce document est proposé en consultation et téléchargement sur la page « Notre agence ».</p>
              {settings.fees_pdf ? (
                <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg" data-testid="settings-fees-current">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText size={20} className="text-red-500 flex-shrink-0" />
                    <span className="text-sm truncate">{settings.fees_pdf_name || "honoraires.pdf"}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${settings.fees_pdf}`} target="_blank" rel="noreferrer" className="text-sm text-[#D4AF37] hover:underline inline-flex items-center gap-1" data-testid="settings-fees-view">
                      Voir <ExternalLink size={14} />
                    </a>
                    <button type="button" onClick={() => setSettings(prev => ({ ...prev, fees_pdf: "", fees_pdf_name: "" }))} className="text-gray-400 hover:text-red-600" title="Retirer" data-testid="settings-fees-remove">
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic" data-testid="settings-fees-empty">Aucun PDF chargé : la page affiche « Barème bientôt disponible ».</p>
              )}
              <input ref={feesInputRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={handleFeesFile} data-testid="settings-fees-file-input" />
              <Button type="button" variant="outline" className="w-full border-dashed border-2 h-14 hover:border-[#D4AF37] hover:text-[#D4AF37]" onClick={() => feesInputRef.current?.click()} disabled={uploadingFees} data-testid="settings-fees-upload-button">
                <Upload size={18} className="mr-2" />
                {uploadingFees ? "Envoi en cours..." : (settings.fees_pdf ? "Remplacer le PDF des honoraires" : "Télécharger le PDF des honoraires (10 Mo max)")}
              </Button>
            </CardContent>
          </Card>

          {/* Offers shortcut */}
          <Card className="border-0 shadow-sm border-l-4 border-l-[#D4AF37]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Tag size={20} className="text-[#D4AF37]" />
                Offres promotionnelles
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <p className="text-sm text-gray-600">Créez, modifiez, publiez, désactivez ou supprimez vos offres commerciales ; choisissez celles affichées sur la page d'accueil. Les dates de validité sont appliquées automatiquement.</p>
              <Link to="/admin/offres" className="flex-shrink-0">
                <Button type="button" className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]" data-testid="settings-offers-link">
                  Gérer les offres <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>

          {/* RGPD retention */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Scale size={20} className="text-[#D4AF37]" />
                Conservation des données (RGPD)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-gray-600">Chaque nuit, le site supprime automatiquement les dossiers locatifs <strong>non retenus</strong> (statut autre que « Approuvé », fichier PDF inclus) et les demandes de contact plus anciennes que ces durées.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="retention_documents_days">Dossiers locatifs non retenus (jours)</Label>
                  <Input id="retention_documents_days" type="number" min={1} value={settings.retention_documents_days ?? 90}
                    onChange={(e) => setSettings(prev => ({ ...prev, retention_documents_days: Number(e.target.value) }))} data-testid="settings-retention-documents" />
                  <p className="text-xs text-gray-500">Recommandation CNIL : 3 mois après la décision (90 jours).</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="retention_contacts_days">Demandes de contact / estimation / gestion (jours)</Label>
                  <Input id="retention_contacts_days" type="number" min={1} value={settings.retention_contacts_days ?? 1095}
                    onChange={(e) => setSettings(prev => ({ ...prev, retention_contacts_days: Number(e.target.value) }))} data-testid="settings-retention-contacts" />
                  <p className="text-xs text-gray-500">Recommandation : 3 ans après le dernier contact (1095 jours).</p>
                </div>
              </div>
              <p className="text-xs text-gray-500">Les copies déjà envoyées par e-mail (boîte location@) doivent être supprimées manuellement dans votre messagerie IONOS : le site n'a pas accès à votre boîte mail.</p>
            </CardContent>
          </Card>

          {/* Hébergement UE */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Server size={20} className="text-[#D4AF37]" />
                Hébergement du site (politique de confidentialité, § 6)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start justify-between gap-4 p-4 bg-[#F9F9F9] rounded-sm">
                <div>
                  <p className="font-medium">Hébergement 100 % Union européenne finalisé</p>
                  <p className="text-xs text-gray-500 mt-1">Activez cet interrupteur le jour où le site tourne sur le serveur européen : le § 6 de la politique de confidentialité bascule automatiquement vers le texte « aucun transfert hors UE » et la mention Cloudflare disparaît du § 10.</p>
                </div>
                <Switch checked={!!settings.hosting_eu} onCheckedChange={(v) => setSettings(prev => ({ ...prev, hosting_eu: v }))} data-testid="settings-hosting-eu" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="hosting_provider">Hébergeur</Label>
                  <Input id="hosting_provider" value={settings.hosting_provider || ""} onChange={(e) => setSettings(prev => ({ ...prev, hosting_provider: e.target.value }))} placeholder="OVHcloud / Scaleway / Hetzner" data-testid="settings-hosting-provider" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hosting_location">Localisation du serveur</Label>
                  <Input id="hosting_location" value={settings.hosting_location || ""} onChange={(e) => setSettings(prev => ({ ...prev, hosting_location: e.target.value }))} placeholder="Gravelines, France" data-testid="settings-hosting-location" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hosting_database">Base de données</Label>
                  <Input id="hosting_database" value={settings.hosting_database || ""} onChange={(e) => setSettings(prev => ({ ...prev, hosting_database: e.target.value }))} placeholder="MongoDB Atlas – région Paris" data-testid="settings-hosting-database" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Legal texts */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Scale size={20} className="text-[#D4AF37]" />
                Textes légaux
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="legal_notice">Mentions légales</Label>
                <Textarea
                  id="legal_notice"
                  rows={10}
                  value={settings.legal_notice || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, legal_notice: e.target.value }))}
                  placeholder="Éditeur du site, forme juridique, SIRET, carte professionnelle, garantie financière, assurance RCP, hébergeur…"
                  data-testid="settings-legal-notice"
                />
                <p className="text-xs text-gray-500">Affiché sur la page « Mentions légales » (lien en pied de page). Les sauts de ligne sont conservés.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="privacy_policy">Politique de confidentialité</Label>
                <Textarea
                  id="privacy_policy"
                  rows={10}
                  value={settings.privacy_policy || ""}
                  onChange={(e) => setSettings(prev => ({ ...prev, privacy_policy: e.target.value }))}
                  placeholder="Laissez vide pour afficher la politique de confidentialité par défaut du site (texte complet RGPD, mis à jour automatiquement avec vos coordonnées et durées de conservation)."
                  data-testid="settings-privacy-policy"
                />
                <p className="text-xs text-gray-500">Affiché sur la page « Politique de confidentialité » (lien en pied de page). Si ce champ est vide, le texte par défaut intégré au site est affiché.</p>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end">
            <Button 
              type="submit"
              className="bg-[#0A0A0A] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A0A0A]"
              disabled={saving}
              data-testid="settings-save-button"
            >
              {saving ? (
                "Enregistrement..."
              ) : (
                <>
                  <Save size={18} className="mr-2" />
                  Enregistrer les modifications
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
